import { z } from 'zod';
import { createCollection, createLiveQueryCollection } from '@tanstack/db';
import { queryCollectionOptions } from "@tanstack/query-db-collection"
import { queryClient } from './querybase';
import { ISettingsModel, LanguageEnum, Settings, ThemeEnum } from '../models/settingsmodel';
import { UserService } from '../services/users-service';
import { authService } from '../services/auth-service';

export const SettingsSchema = z.object({
  version: z.number(),
  language: z.nativeEnum(LanguageEnum),
  theme: z.nativeEnum(ThemeEnum),
});


export function calcInitialLanguage(): LanguageEnum {
  // match navigator.languages against the available values in the enum LanguageEnum and select the most appropriate or english
  const availableLanguages = Object.values(LanguageEnum);

  for (const lang of navigator.languages) {
    if (availableLanguages.includes(lang as LanguageEnum)) {
      return lang as LanguageEnum;
    }
    const baseLang = lang.split('-')[0];
    if (availableLanguages.includes(baseLang as LanguageEnum)) {
      return baseLang as LanguageEnum;
    }
  }

  return LanguageEnum.en; // Default to English if no match
}

export async function settingsQueryFn() {
  if (authService.state.user) {
    const res = await UserService.getSettings(authService.state.user.userUUID);
    return [res ?? new Settings({ version: 1, theme: ThemeEnum.dark, language: calcInitialLanguage() })];
  } else {
    return [new Settings({ version: 1, theme: ThemeEnum.dark, language: calcInitialLanguage() })];
  }
}

export function settingsGetKey(item: ISettingsModel) {
  return item.version;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function settingsOnUpdate({ transaction }: any) {
  const { changes: newSettings, original: oldSettings } = transaction.mutations[0];
  await UserService.setSettings(authService.state.user!.userUUID, { ...oldSettings, ...newSettings });
}

export const settingsCollection = createCollection(
  queryCollectionOptions({
    queryClient: queryClient,
    queryKey: [ 'settings' ],
    queryFn: settingsQueryFn,
    getKey: settingsGetKey,
    schema: SettingsSchema,

    onUpdate: settingsOnUpdate,

    // refetch should not really be relevant, assuming user only uses 1 client
    refetchInterval: 24*60*60000, // once a day

  })
);

export const createSettingsLiveQuery = () => {
  return createLiveQueryCollection({
    query: (q) => q.from({ settings: settingsCollection }),
    getKey: (settings) => settings.version,
    onUpdate: async ({ transaction }) => {
      const { changes: newSettings, original: oldSettings } = transaction.mutations[0];
      const tx = settingsCollection.update(oldSettings.version, (draft) => {
        if (newSettings.theme) draft.theme = newSettings.theme;
        if (newSettings.language) draft.language = newSettings.language;
      });
      await tx.isPersisted.promise
    },
  });
}
